/**
 * Register.jsx - Complete Registration Form
 * ==========================================
 * Features:
 * - Cascading location dropdowns (Country → State → City)
 * - Form validation with inline errors
 * - Password strength indicator
 * - Custom "Hold to verify" captcha
 * - Conditional company field
 * - Accessibility compliant
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff, Loader2, Check, AlertCircle, Shield } from 'lucide-react';
import { useRoles } from '../hooks/useRoles';
import supabase from '../lib/supabase';
import MapboxLocationSelector from '../components/MapboxLocationSelector';
import SocialLoginButtons from '../components/auth/SocialLoginButtons';
import { parseAuthError, authLog } from '../utils/authUtils';

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

/**
 * STRONG PASSWORD VALIDATION
 * Requirements:
 * - Min 8 characters
 * - 1 uppercase letter
 * - 1 lowercase letter
 * - 1 number
 * - 1 special character
 */
const validatePassword = (password) => {
  const checks = {
    hasMinLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~]/.test(password)
  };
  
  const isValid = Object.values(checks).every(Boolean);
  
  return { ...checks, isValid };
};

const getPasswordStrength = (password) => {
  if (!password) return { level: 0, label: '', color: '' };
  
  const checks = validatePassword(password);
  const passedChecks = Object.values(checks).filter(Boolean).length - 1; // -1 for isValid
  
  if (passedChecks <= 1) return { level: 1, label: 'Debole', color: 'bg-red-500' };
  if (passedChecks === 2) return { level: 2, label: 'Insufficiente', color: 'bg-orange-500' };
  if (passedChecks === 3) return { level: 3, label: 'Discreta', color: 'bg-yellow-500' };
  if (passedChecks === 4) return { level: 4, label: 'Buona', color: 'bg-lime-500' };
  return { level: 5, label: 'Forte', color: 'bg-emerald-500' };
};

// ============================================================================
// PASSWORD CHECKLIST COMPONENT
// ============================================================================

const PasswordChecklist = ({ password }) => {
  const checks = validatePassword(password);
  
  const requirements = [
    { key: 'hasMinLength', label: 'Minimo 8 caratteri', passed: checks.hasMinLength },
    { key: 'hasUppercase', label: 'Una lettera maiuscola (A-Z)', passed: checks.hasUppercase },
    { key: 'hasLowercase', label: 'Una lettera minuscola (a-z)', passed: checks.hasLowercase },
    { key: 'hasNumber', label: 'Un numero (0-9)', passed: checks.hasNumber },
    { key: 'hasSpecial', label: 'Un carattere speciale (!@#$...)', passed: checks.hasSpecial },
  ];
  
  if (!password) return null;
  
  return (
    <div className="mt-3 p-3 rounded-lg bg-white/5 border border-white/10">
      <p className="text-xs text-white/50 mb-2 font-medium">Requisiti password:</p>
      <div className="space-y-1.5">
        {requirements.map(req => (
          <div key={req.key} className="flex items-center gap-2">
            <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
              req.passed ? 'bg-emerald-500/20' : 'bg-white/10'
            }`}>
              {req.passed ? (
                <Check className="w-3 h-3 text-emerald-400" />
              ) : (
                <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
              )}
            </div>
            <span className={`text-xs transition-colors ${
              req.passed ? 'text-emerald-400' : 'text-white/40'
            }`}>
              {req.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// CUSTOM CAPTCHA COMPONENT
// ============================================================================

const HoldToVerifyCaptcha = ({ onVerified, verified }) => {
  const [progress, setProgress] = useState(0);
  const [isHolding, setIsHolding] = useState(false);
  const intervalRef = useRef(null);
  const HOLD_DURATION = 2000; // 2 seconds
  const UPDATE_INTERVAL = 50;
  
  const startHold = useCallback(() => {
    if (verified) return;
    setIsHolding(true);
    setProgress(0);
    
    intervalRef.current = setInterval(() => {
      setProgress((prev) => {
        const next = prev + (UPDATE_INTERVAL / HOLD_DURATION) * 100;
        if (next >= 100) {
          clearInterval(intervalRef.current);
          setIsHolding(false);
          onVerified(true);
          return 100;
        }
        return next;
      });
    }, UPDATE_INTERVAL);
  }, [verified, onVerified]);
  
  const stopHold = useCallback(() => {
    if (verified) return;
    clearInterval(intervalRef.current);
    setIsHolding(false);
    setProgress(0);
  }, [verified]);
  
  useEffect(() => {
    return () => clearInterval(intervalRef.current);
  }, []);
  
  return (
    <div className="space-y-2">
      <Label className="text-white/60 text-xs uppercase tracking-wider">
        Verifica di sicurezza *
      </Label>
      <button
        type="button"
        onMouseDown={startHold}
        onMouseUp={stopHold}
        onMouseLeave={stopHold}
        onTouchStart={startHold}
        onTouchEnd={stopHold}
        disabled={verified}
        className={`relative w-full h-12 rounded-lg border overflow-hidden transition-all ${
          verified 
            ? 'bg-emerald-500/20 border-emerald-500/50 cursor-default' 
            : 'bg-white/5 border-white/20 hover:border-sr-gold/50 cursor-pointer'
        }`}
      >
        {/* Progress bar */}
        <div 
          className={`absolute left-0 top-0 h-full transition-all ${
            verified ? 'bg-emerald-500/30' : 'bg-sr-gold/30'
          }`}
          style={{ width: `${progress}%` }}
        />
        
        {/* Content */}
        <div className="relative flex items-center justify-center gap-2 h-full">
          {verified ? (
            <>
              <Check className="w-5 h-5 text-emerald-400" />
              <span className="text-sm text-emerald-400 font-medium">Verificato</span>
            </>
          ) : isHolding ? (
            <>
              <Shield className="w-5 h-5 text-sr-gold-light animate-pulse" />
              <span className="text-sm text-sr-gold-light">Tieni premuto...</span>
            </>
          ) : (
            <>
              <Shield className="w-5 h-5 text-white/60" />
              <span className="text-sm text-white/60">Tieni premuto per verificare</span>
            </>
          )}
        </div>
      </button>
    </div>
  );
};

// ============================================================================
// PASSWORD STRENGTH INDICATOR
// ============================================================================

const PasswordStrengthIndicator = ({ password }) => {
  const strength = getPasswordStrength(password);
  
  if (!password) return null;
  
  return (
    <div className="space-y-1 mt-2">
      <div className="flex gap-1">
        {[1, 2, 3, 4].map((level) => (
          <div 
            key={level}
            className={`h-1 flex-1 rounded-full transition-all ${
              level <= strength.level ? strength.color : 'bg-white/10'
            }`}
          />
        ))}
      </div>
      <p className={`text-xs ${
        strength.level === 1 ? 'text-red-400' :
        strength.level === 2 ? 'text-orange-400' :
        strength.level === 3 ? 'text-yellow-400' :
        'text-emerald-400'
      }`}>
        Password: {strength.label}
      </p>
    </div>
  );
};

// ============================================================================
// FORM FIELD ERROR
// ============================================================================

const FieldError = ({ error }) => {
  if (!error) return null;
  return (
    <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
      <AlertCircle className="w-3 h-3" />
      {error}
    </p>
  );
};

// ============================================================================
// MAIN REGISTER COMPONENT
// ============================================================================

const Register = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: '',
    company: '',
    role: '',
    // Location from Mapbox
    location: null
  });
  
  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isHumanVerified, setIsHumanVerified] = useState(false);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  
  // Fetch roles from DB
  const { roles, loading: rolesLoading } = useRoles('it');
  
  // Redirect if already logged in
  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);
  
  // Validate form
  useEffect(() => {
    const newErrors = {};
    
    // First name
    if (touched.first_name && !formData.first_name.trim()) {
      newErrors.first_name = 'Nome richiesto';
    }
    
    // Last name
    if (touched.last_name && !formData.last_name.trim()) {
      newErrors.last_name = 'Cognome richiesto';
    }
    
    // Email
    if (touched.email) {
      if (!formData.email) {
        newErrors.email = 'Email richiesta';
      } else if (!validateEmail(formData.email)) {
        newErrors.email = 'Email non valida';
      }
    }
    
    // Password
    if (touched.password) {
      const pwdValidation = validatePassword(formData.password);
      if (!formData.password) {
        newErrors.password = 'Password richiesta';
      } else if (!pwdValidation.isValid) {
        newErrors.password = 'La password non soddisfa tutti i requisiti';
      }
    }
    
    // Confirm password
    if (touched.confirm_password) {
      if (!formData.confirm_password) {
        newErrors.confirm_password = 'Conferma password richiesta';
      } else if (formData.password !== formData.confirm_password) {
        newErrors.confirm_password = 'Le password non coincidono';
      }
    }
    
    // Role
    if (touched.role && !formData.role) {
      newErrors.role = 'Ruolo richiesto';
    }
    
    // Company (conditional)
    if (touched.company && formData.role && formData.role !== 'private_client' && !formData.company.trim()) {
      newErrors.company = 'Azienda richiesta per professionisti';
    }
    
    // Location (Mapbox)
    if (touched.location && !formData.location) {
      newErrors.location = 'Città richiesta';
    }
    
    setErrors(newErrors);
  }, [formData, touched]);
  
  // Check if form is valid
  const isFormValid = () => {
    // Required fields
    if (!formData.first_name.trim()) return false;
    if (!formData.last_name.trim()) return false;
    if (!validateEmail(formData.email)) return false;
    if (!validatePassword(formData.password).isValid) return false;
    if (formData.password !== formData.confirm_password) return false;
    if (!formData.role) return false;
    
    // Conditional company
    if (formData.role !== 'private_client' && !formData.company.trim()) return false;
    
    // Location (Mapbox)
    if (!formData.location) return false;
    
    // Captcha
    if (!isHumanVerified) return false;
    
    return true;
  };
  
  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle blur (mark as touched)
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };
  
  // Handle role change
  const handleRoleChange = (value) => {
    setFormData(prev => ({ ...prev, role: value }));
    handleBlur('role');
  };
  
  // Handle location change (Mapbox)
  const handleLocationChange = (location) => {
    setFormData(prev => ({ ...prev, location }));
    if (location) {
      handleBlur('location');
    }
  };
  
  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all as touched
    const allFields = ['first_name', 'last_name', 'email', 'password', 'confirm_password', 'role', 'company', 'location'];
    setTouched(allFields.reduce((acc, f) => ({ ...acc, [f]: true }), {}));
    
    if (!isFormValid()) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    setLoading(true);
    
    authLog('Register: Starting signup', { email: formData.email });
    
    try {
      // STEP 1 — CHECK IF USER EXISTS
      authLog('Register: Checking if user already exists');
      
      // Supabase signUp returns user even if exists (with identities check)
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password
      });
      
      if (error) {
        authLog('Register: Signup error', { error: error.message });
        
        // Handle specific error for existing user
        if (error.message.includes('already registered') || 
            error.message.includes('User already registered')) {
          toast.error('Questo indirizzo email è già registrato. Effettua il login.');
          navigate('/login', { state: { email: formData.email } });
          return;
        }
        
        throw error;
      }
      
      const user = data.user;
      
      if (!user) {
        throw new Error('User not created');
      }
      
      // Check if user already exists (Supabase returns user but no session for existing)
      // Also check identities - if empty, user exists but used different auth method
      if (user.identities && user.identities.length === 0) {
        authLog('Register: User already exists (no identities)', { email: formData.email });
        toast.error('Questo account esiste già. Prova ad accedere con Google o effettua il login.');
        navigate('/login', { state: { email: formData.email } });
        return;
      }
      
      authLog('Register: User created', { userId: user.id, email: user.email });
      
      // STEP 2 — GESTIONE EMAIL CONFIRMATION
      if (!data.session) {
        authLog('Register: No session, email confirmation required');
        toast.success("Controlla la tua email per confermare l'account");
        navigate('/login', { state: { emailConfirmation: true } });
        return;
      }
      
      authLog('Register: Session created, updating profile');
      
      // Wait for trigger to create profile
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // STEP 3 — UPDATE PROFILE (NON INSERT)
      // Extract location data from Mapbox selection
      const loc = formData.location;
      
      // Since user provided all required fields during registration,
      // mark onboarding as COMPLETE
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          first_name: formData.first_name,
          last_name: formData.last_name,
          full_name: `${formData.first_name} ${formData.last_name}`,
          company: formData.company || null,
          role_professional: formData.role,
          role_system: 'user',
          user_type: formData.role === 'private_client' ? 'private' : 'pro',
          // Location from Mapbox
          country_code: loc?.country_code || '',
          country_name: loc?.country_name || '',
          state_code: loc?.state_code || '',
          state_name: loc?.state_name || '',
          city: loc?.city || '',
          city_id: loc?.city_id || '',
          lat: loc?.lat || null,
          lng: loc?.lng || null,
          // IMPORTANTE: Onboarding completato perché tutti i dati sono stati forniti
          onboarding_completed: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
      
      if (profileError) {
        authLog('Register: Profile update error', { error: profileError.message });
        // Non-critical - profile created by trigger, but log for debugging
        console.warn('Profile update warning:', profileError);
      } else {
        authLog('Register: Profile updated successfully with onboarding_completed=true');
      }
      
      toast.success('Account creato con successo!');
      
      // Utente ha fornito tutti i dati → vai direttamente alla dashboard
      authLog('Register: All data provided, navigating to dashboard');
      navigate('/dashboard', { replace: true });
      
    } catch (error) {
      authLog('Register: Error', { error: error.message });
      toast.error(parseAuthError(error));
    } finally {
      setLoading(false);
    }
  };
  
  // Company required check
  const isCompanyRequired = formData.role && formData.role !== 'private_client';
  
  return (
    <div className="min-h-screen bg-[#0a0a0f] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Back link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna alla home
          </Link>
          
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-white mb-2" data-testid="register-title">
              Crea il tuo account
            </h1>
            <p className="text-white/60">
              Inizia a esplorare i materiali più esclusivi
            </p>
          </div>
          
          {/* Social Login Buttons */}
          <div className="mb-6">
            <SocialLoginButtons mode="register" />
          </div>
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">
                  Nome *
                </Label>
                <Input
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  onBlur={() => handleBlur('first_name')}
                  placeholder="Mario"
                  className={`bg-white/5 border-white/20 text-white placeholder:text-white/30 focus:border-sr-gold ${
                    errors.first_name ? 'border-red-500' : ''
                  }`}
                  aria-invalid={!!errors.first_name}
                  data-testid="register-first-name"
                />
                <FieldError error={errors.first_name} />
              </div>
              <div>
                <Label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">
                  Cognome *
                </Label>
                <Input
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  onBlur={() => handleBlur('last_name')}
                  placeholder="Rossi"
                  className={`bg-white/5 border-white/20 text-white placeholder:text-white/30 focus:border-sr-gold ${
                    errors.last_name ? 'border-red-500' : ''
                  }`}
                  aria-invalid={!!errors.last_name}
                  data-testid="register-last-name"
                />
                <FieldError error={errors.last_name} />
              </div>
            </div>
            
            {/* Email */}
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">
                Email *
              </Label>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={() => handleBlur('email')}
                placeholder="mario.rossi@email.com"
                className={`bg-white/5 border-white/20 text-white placeholder:text-white/30 focus:border-sr-gold ${
                  errors.email ? 'border-red-500' : ''
                }`}
                aria-invalid={!!errors.email}
                data-testid="register-email"
              />
              <FieldError error={errors.email} />
            </div>
            
            {/* Password */}
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">
                Password *
              </Label>
              <div className="relative">
                <Input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={() => handleBlur('password')}
                  placeholder="••••••••"
                  className={`bg-white/5 border-white/20 text-white placeholder:text-white/30 focus:border-sr-gold pr-10 ${
                    errors.password ? 'border-red-500' : ''
                  }`}
                  aria-invalid={!!errors.password}
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <PasswordChecklist password={formData.password} />
              <PasswordStrengthIndicator password={formData.password} />
              <FieldError error={errors.password} />
            </div>
            
            {/* Confirm Password */}
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">
                Conferma Password *
              </Label>
              <div className="relative">
                <Input
                  name="confirm_password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirm_password}
                  onChange={handleChange}
                  onBlur={() => handleBlur('confirm_password')}
                  placeholder="••••••••"
                  className={`bg-white/5 border-white/20 text-white placeholder:text-white/30 focus:border-sr-gold pr-10 ${
                    errors.confirm_password ? 'border-red-500' : ''
                  }`}
                  aria-invalid={!!errors.confirm_password}
                  data-testid="register-confirm-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {formData.confirm_password && formData.password === formData.confirm_password && (
                <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Le password coincidono
                </p>
              )}
              <FieldError error={errors.confirm_password} />
            </div>
            
            {/* Role */}
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">
                Ruolo Professionale *
              </Label>
              {rolesLoading ? (
                <div className="h-10 bg-white/5 rounded-lg flex items-center justify-center">
                  <Loader2 className="w-4 h-4 text-white/40 animate-spin" />
                </div>
              ) : (
                <Select value={formData.role} onValueChange={handleRoleChange}>
                  <SelectTrigger 
                    className={`bg-white/5 border-white/20 text-white focus:border-sr-gold ${
                      errors.role ? 'border-red-500' : ''
                    }`}
                    data-testid="register-role-select"
                  >
                    <SelectValue placeholder="Seleziona il tuo ruolo" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(role => (
                      <SelectItem key={role.code} value={role.code}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <FieldError error={errors.role} />
            </div>
            
            {/* Company (conditional) */}
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">
                Azienda {isCompanyRequired ? '*' : '(opzionale)'}
              </Label>
              <Input
                name="company"
                value={formData.company}
                onChange={handleChange}
                onBlur={() => handleBlur('company')}
                placeholder="Nome azienda o studio"
                className={`bg-white/5 border-white/20 text-white placeholder:text-white/30 focus:border-sr-gold ${
                  errors.company ? 'border-red-500' : ''
                }`}
                aria-invalid={!!errors.company}
                data-testid="register-company"
              />
              <FieldError error={errors.company} />
            </div>
            
            {/* Location (Mapbox) */}
            <div className="relative z-50">
              <MapboxLocationSelector
                value={formData.location}
                onChange={handleLocationChange}
                placeholder="Cerca la tua città..."
                label="Città"
                required
                error={errors.location}
              />
            </div>
            
            {/* Captcha */}
            <HoldToVerifyCaptcha 
              onVerified={setIsHumanVerified} 
              verified={isHumanVerified} 
            />
            
            {/* Submit button */}
            <Button
              type="submit"
              disabled={!isFormValid() || loading}
              className="w-full bg-sr-gold hover:bg-sr-gold-hover text-black font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="register-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creazione account...
                </>
              ) : (
                'Crea Account'
              )}
            </Button>
            
            {/* Login link */}
            <p className="text-center text-white/60 text-sm">
              Hai già un account?{' '}
              <Link to="/login" className="text-sr-gold-light hover:underline">
                Accedi
              </Link>
            </p>
          </form>
        </div>
      </div>
      
      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&h=1600&fit=crop)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0f] via-[#0a0a0f]/50 to-transparent" />
        <div className="absolute bottom-12 left-12 max-w-md">
          <h2 className="text-3xl font-bold text-white mb-4">
            La pietra naturale, come non l'hai mai vista
          </h2>
          <p className="text-white/70">
            Accedi a oltre 500 materiali esclusivi, confronta, crea moodboard e condividi con i tuoi clienti.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
